<script lang="ts">
	import Button from '$lib/Button.svelte';
	import Social from '$lib/Social.svelte';
	import dayjs from 'dayjs';
	import { ChevronRight } from 'lucide-svelte';
	import { userStore } from './stores/user';

	interface Props {
		tools: Populated<BaseTool, never>[];
		footerHeight: number;
	}

	let { tools, footerHeight = $bindable() }: Props = $props();

	let footerItems = [
		{
			label: 'Insights',
			children: [
				{ label: 'Latest', url: '/insights' },
				{ label: 'Trending', url: '/insights/trending' }
			]
		},
		{
			label: 'Tools',
			children: tools.map((tool) => ({
				label: tool.name,
				url: tool.url
			}))
		},
		{
			label: 'Markets',
			children: [
				{ label: 'Singapore securities', url: '/securities' },
				{ label: 'Singapore REITs', url: '/reits' },
				{ label: 'Singapore ETFs', url: '/etfs' },
				{ label: 'Singapore Mutual Funds', url: '/mutual-funds' }
			]
		},
		{
			label: 'About',
			children: [
				{ label: 'Company', url: '/about-us' },
				{ label: 'Partners', url: '/partners' }
			]
		}
	];
</script>

<footer
	bind:clientHeight={footerHeight}
	class="page-wrapper bg-white-chocolate absolute bottom-0 w-full py-16"
>
	<div class="m-auto max-w-screen-xl">
		<div class="mb-8 flex w-full flex-col">
			{#if !$userStore.signedIn}
				<section
					class="element-visibility flex flex-col justify-center gap-8 lg:flex-row lg:items-center"
					data-type="email-subscription"
					data-content="email-subscription-footer"
				>
					<div class="basis-1/2">
						<p class="body-bold mb-2">Weekly Sprout Newsletter 🌱</p>
						<header class="h1-serif mb-2 text-left">Gain financial insights in minutes</header>
						<p class="body-regular mb-2">
							The newsletter that keeps you up-to-date on the financial markets.<br />
							It’s fun, informative, and free!
						</p>
						<a href="/newsletter" class="link card-bold inline-flex items-center">
							Check out Weekly Sprout Newsletter
							<ChevronRight class="h-6 w-6" />
						</a>
					</div>
					<!-- Begin Mailchimp Signup Form -->
					<form
						action="https://canopyresearch.us6.list-manage.com/subscribe/post?u=b1e286f7d69da07af7f83751a&amp;id=28d23bffd6"
						method="post"
						name="mc-embedded-subscribe-form"
						class="flex basis-1/2 flex-col items-center gap-4 lg:flex-row"
						target="_blank"
						novalidate
					>
						<input
							type="email"
							value=""
							name="EMAIL"
							placeholder="Your email here."
							class="h-12 w-full rounded-3xl p-4"
						/>
						<Button
							type="submit"
							value="Subscribe"
							name="subscribe"
							id="mc-embedded-subscribe"
							class="w-full lg:w-auto"
							data-type="email-subscription"
							data-content="email-subscription-footer"
						>
							Subscribe
						</Button>

						<!-- real people should not fill this in and expect good things - do not remove this or risk form bot signups-->
						<div class="absolute left-[-5000px]" aria-hidden="true">
							<input
								type="text"
								name="b_b1e286f7d69da07af7f83751a_28d23bffd6"
								tabindex="-1"
								value=""
							/>
						</div>
					</form>
					<!--End mc_embed_signup-->
				</section>
				<hr class="border-gray-light my-8 border-1" />
			{/if}
			<nav
				aria-label="footer"
				class="flex flex-col justify-between gap-8 whitespace-nowrap text-black lg:pr-8"
			>
				<div class="grid gap-x-8 gap-y-8 lg:grid-flow-col">
					{#each footerItems as item}
						<section>
							<header class="body-bold mb-4">{item.label}</header>
							<ul class="grid gap-4">
								{#each item.children as child}
									<li><a href={child.url} class="hover:text-green-primary">{child.label}</a></li>
								{/each}
							</ul>
						</section>
					{/each}
					<section class="ml-16 hidden xl:block">
						<header class="h2-serif mb-6">Connect with us now</header>
						<Social platform="linkedin" href="https://www.linkedin.com/company/growbeansprout" />
						<Social platform="facebook" href="https://www.facebook.com/growbeansprout" />
						<Social platform="instagram" href="https://www.instagram.com/growbeansprout" />
						<Social platform="telegram" href="https://t.me/BeansproutAnnouncement" />
					</section>
				</div>

				<ul class="index-bold flex flex-col gap-3 lg:flex-row">
					{#each [['Glossary', '/glossary'], ['Privacy Policy', '/privacy'], ['Disclaimer', '/disclaimer'], ['Terms of Use', '/terms-of-use'], ['Contact', '/contact']] as [label, url]}
						<li>
							<a class="footer-nav-item" href={url}>{label}</a>
						</li>
					{/each}
				</ul>
			</nav>
		</div>
		<section class="mb-8 xl:hidden">
			<header class="h2-serif mb-6">Connect with us now</header>
			<Social platform="linkedin" href="https://www.linkedin.com/company/growbeansprout" />
			<Social platform="facebook" href="https://www.facebook.com/growbeansprout" />
			<Social platform="instagram" href="https://www.instagram.com/growbeansprout" />
			<Social platform="telegram" href="https://t.me/BeansproutAnnouncement" />
		</section>
		<section class="text-gray-mid text-[0.625rem] leading-snug">
			<p>© {dayjs().year()} Beansprout. All rights reserved.</p>
		</section>
	</div>
</footer>

<style lang="postcss">
	@reference '../app.css';

	:global(a.footer-nav-item) {
		@apply inline-block font-medium;

		&:after {
			content: '';
			@apply float-left h-[2px] w-full rounded;
		}
		&:hover {
			&:after {
				@apply bg-black;
			}
		}
	}
</style>
